import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { addTag, createEntry } from '../../api/entries'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import type { Entry } from '../../api/entries'
import { SchemePicker } from '../SchemePicker'
import type { ColorTheme } from './colorTheme'
import { useThemeStore } from '../../store/theme'
import { useAuthStore } from '../../store/auth'
import { useDefaultsStore } from '../../store/defaults'
import { useMediaUpload, markdownTag } from './useMediaUpload'
import { MediaPicker } from './MediaPicker'
import { TagInput } from './TagInput'
import { MarkdownRenderer } from './MarkdownRenderer'
import { TodoEntryDialog } from './TodoEntryDialog'
import { AssigneeInput } from './AssigneeInput'

interface Props {
  onCreated?: (entry: Entry) => void
  initialTags?: string[]
  entryType?: string
  initialBody?: string
}

export function EntryComposer({ onCreated, initialTags, entryType = 'journal', initialBody }: Props) {
  const { userTheme } = useThemeStore()
  const username = useAuthStore((s) => s.username)
  const emailVerified = useAuthStore((s) => s.emailVerified)
  const emailEnabled = useAuthStore((s) => s.emailEnabled)
  const { defaults } = useDefaultsStore()
  const [name, setName] = useState('')
  const [content, setContent] = useState(initialBody ?? '')
  const [privacy, setPrivacy] = useState(() => defaults.entry)
  const [allowReplies, setAllowReplies] = useState(false)
  const [colorTheme, setColorTheme] = useState<ColorTheme | null>(userTheme ?? null)
  const [tags, setTags] = useState<string[]>(initialTags ?? [])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [assignee, setAssignee] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [todoDialogOpen, setTodoDialogOpen] = useState(false)
  const [todoInsertPos, setTodoInsertPos] = useState(0)

  const { fileInputRef, handleFileChange, handlePaste, insertAtCursor, uploadingCount, uploadError } = useMediaUpload({
    username,
    textareaRef,
    content,
    setContent,
  })
  const [showPicker, setShowPicker] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const isDirty = name !== '' || content !== ''
  useUnsavedChangesGuard(isDirty)

  function insertTodo() {
    const el = textareaRef.current
    if (!el) return
    setTodoInsertPos(el.selectionStart)
    setTodoDialogOpen(true)
  }

  function handleTodoInserted(markdown: string) {
    const el = textareaRef.current
    const pos = todoInsertPos
    const before = content.slice(0, pos)
    const after = content.slice(pos)
    const needsNewline = before.length > 0 && !before.endsWith('\n')
    const insertion = (needsNewline ? '\n' : '') + markdown
    setContent(before + insertion + after)
    setTodoDialogOpen(false)
    requestAnimationFrame(() => {
      if (el) {
        el.selectionStart = el.selectionEnd = pos + insertion.length
        el.focus()
      }
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart
    if (pos !== el.selectionEnd) return

    const lineStart = content.lastIndexOf('\n', pos - 1) + 1
    const currentLine = content.slice(lineStart, pos)
    const match = currentLine.match(/^- \[[ xX]\] (.*)$/)
    if (!match) return

    e.preventDefault()
    const afterPrefix = match[1]

    if (afterPrefix.trim() === '') {
      const newContent = content.slice(0, lineStart) + content.slice(pos)
      setContent(newContent)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = lineStart
      })
    } else {
      const insertion = '\n- [ ] '
      const newContent = content.slice(0, pos) + insertion + content.slice(el.selectionEnd)
      setContent(newContent)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = pos + insertion.length
      })
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      let entry = await createEntry({
        name: name.trim(),
        content,
        mimetype: 'text/markdown',
        privacy,
        allow_replies: allowReplies,
        color_theme: colorTheme ?? undefined,
        entry_type: entryType,
        todo_assignee_username: entryType === 'todo' && assignee.trim() ? assignee.trim() : undefined,
        todo_due_date: entryType === 'todo' && dueDate ? dueDate : undefined,
      })
      for (const tag of tags) {
        await addTag(entry.id, tag)
        entry = { ...entry, tags: [...entry.tags, { name: tag, tagger_username: username ?? '' }] }
      }
      setName('')
      setContent('')
      setAllowReplies(false)
      setColorTheme(userTheme ?? null)
      setTags(initialTags ?? [])
      setAssignee('')
      setDueDate('')
      onCreated?.(entry)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to create entry')
    } finally {
      setSubmitting(false)
    }
  }

  if (emailEnabled && emailVerified === false) {
    return (
      <div style={{ border: '1px solid var(--color-fg)', padding: '1rem', marginBottom: '2rem', opacity: 0.85 }}>
        <p style={{ margin: '0 0 0.5rem' }}>verify your email address to start posting.</p>
        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.7 }}>
          go to <Link to="/settings">settings</Link> to send a verification link.
        </p>
      </div>
    )
  }

  return (
  <>
    <form onSubmit={handleSubmit} style={{ border: '1px solid var(--color-fg)', padding: '1rem', marginBottom: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.5rem 0' }}>
        <input
          placeholder="title"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: 1 }}
        />
        <select value={privacy} onChange={(e) => setPrivacy(Number(e.target.value))} style={{ width: 'auto' }}>
          <option value={0}>private</option>
          <option value={1}>friends</option>
          <option value={2}>friends of friends</option>
          <option value={3}>public</option>
        </select>
      </div>

      {entryType === 'todo' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <AssigneeInput
            value={assignee}
            onChange={setAssignee}
            currentUsername={username ?? null}
            style={{ flex: 1 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', flexShrink: 0 }}>
            <span style={{ opacity: 0.7 }}>due</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label="due date"
              style={{ width: 'auto' }}
            />
            {dueDate && (
              <button
                type="button"
                onClick={() => setDueDate('')}
                style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.75rem', padding: '0 0.2rem', opacity: 0.6 }}
              >
                clear
              </button>
            )}
          </label>
        </div>
      )}

      <div style={{ marginTop: '0.5rem' }}>
        <div style={{ position: 'relative' }}>
          <textarea
            ref={textareaRef}
            placeholder="write in markdown…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={8}
            style={{ width: '100%', display: 'block', visibility: showPreview ? 'hidden' : 'visible' }}
          />
          {showPreview && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '1px solid var(--color-fg)',
                padding: '0.5rem 1rem',
                overflowY: 'auto',
                background: 'var(--color-bg)',
              }}
            >
              {content
                ? <MarkdownRenderer entryId="" authorUsername="" content={content} colorTheme={colorTheme} />
                : <span style={{ opacity: 0.5 }}>nothing to preview</span>
              }
            </div>
          )}
        </div>
        <div style={{ border: '1px solid var(--color-fg)', borderTop: 'none', display: 'flex', alignItems: 'center', padding: '0.3rem 0.5rem', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <SchemePicker
            colorTheme={colorTheme}
            onColorThemeChange={setColorTheme}
            username={username}
          />
          <button
            type="button"
            onClick={() => setShowPreview((p) => {
              if (p) requestAnimationFrame(() => textareaRef.current?.focus())
              return !p
            })}
            style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem' }}
          >
            {showPreview ? 'edit' : 'preview'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-label="attach files"
          />
          {!showPreview && (
            <button
              type="button"
              onClick={insertTodo}
              style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem', marginLeft: 'auto' }}
            >
              + todo
            </button>
          )}
          {!showPreview && username && (
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem' }}
            >
              + media
            </button>
          )}
          {uploadingCount > 0 && (
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>uploading…</span>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
        <TagInput
          username={username}
          onSubmit={(tag) => setTags((prev) => prev.includes(tag) ? prev : [...prev, tag])}
          excludeTags={tags}
          disabled={submitting}
          inputStyle={{ fontSize: '0.85rem' }}
          buttonStyle={{ width: 'auto', fontSize: '0.85rem' }}
        />
      </div>

      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1rem', background: 'var(--color-fg)', color: 'var(--color-bg)', padding: '0.1rem 0.4rem', borderRadius: 2, fontSize: '0.85rem' }}>
              #{tag}
              <button
                type="button"
                onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.1rem', lineHeight: 1, fontSize: '1rem' }}
                aria-label={`remove tag ${tag}`}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" disabled={submitting || uploadingCount > 0}>
          {submitting ? 'posting…' : 'post'}
        </button>
        {privacy !== 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={allowReplies}
              onChange={(e) => setAllowReplies(e.target.checked)}
            />
            allow replies
          </label>
        )}
      </div>

      {(error || uploadError) && (
        <p style={{ color: 'var(--color-1)', marginTop: '0.5rem' }}>{error || uploadError}</p>
      )}

      {showPicker && username && (
        <MediaPicker
          username={username}
          onSelect={(item) => {
            insertAtCursor(markdownTag(item.filename, item.url, item.mimetype))
            setShowPicker(false)
          }}
          onUpload={() => {
            setShowPicker(false)
            fileInputRef.current?.click()
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </form>
    {todoDialogOpen && username && (
      <TodoEntryDialog
        defaultPrivacy={privacy}
        defaultColorTheme={colorTheme}
        username={username}
        onInserted={handleTodoInserted}
        onClose={() => setTodoDialogOpen(false)}
      />
    )}
  </>
  )
}
